import React from "react";
import { formatIcelandicNumber } from "@/utils/constants";
import { PAGE, TITLE, HR, HR_LIGHT, SH, NAVY, fmtDate, Footer } from "./printHelpers";

const FS = { fontSize: "8.5px", lineHeight: 1.35 };
const FS_SH = { ...SH, fontSize: "9.5px", margin: "6px 0 2px" };
const FS_TD = { padding: "2px 4px", borderBottom: "0.5px solid #ddd", fontSize: "8.5px" };
const FS_TH = { ...FS_TD, fontWeight: 700, background: "#f0f2f7", color: NAVY };

const FRow = ({ label, value }) => (
  <div style={{ display: "flex", gap: 8, padding: "1px 0" }}>
    <span style={{ fontWeight: 600, minWidth: 120 }}>{label}</span>
    <span>{value || "—"}</span>
  </div>
);

const PageThree = ({ form }) => {
  const vl = form?.vehicleLookupData || {};
  const fer = vl.ferilskra || {};
  const tech = fer.technical || {};
  const fees = fer.fees || {};
  const v = form?.vehicle || {};
  const owners = vl.owners || [];
  const operators = vl.operators || [];
  const inspections = fer.inspections || [];
  const registrations = fer.registrations || [];
  const damages = fer.damages || [];
  const changes = fer.generalChanges || [];
  const now = new Date();
  const timestamp = `${now.getDate().toString().padStart(2,"0")}.${(now.getMonth()+1).toString().padStart(2,"0")}.${now.getFullYear()} kl. ${now.getHours().toString().padStart(2,"0")}:${now.getMinutes().toString().padStart(2,"0")}`;

  if (!vl.fastanumer && !v.fastanumer) return null;

  return (
    <div style={{ ...PAGE, ...FS, pageBreakBefore: "always" }}>
      <div style={{ textAlign: "center", marginBottom: 8 }}>
        <div style={{ ...TITLE, fontSize: "13px" }}>
          Ferill ökutækisins {vl.fastanumer || v.fastanumer} í ökutækjaskrá
        </div>
        <div style={{ fontSize: "8px", color: "#666" }}>{timestamp}</div>
      </div>
      <hr style={HR} />

      {/* 1. Lýsing ökutækis */}
      <div style={FS_SH}>1. Lýsing ökutækis</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <FRow label="Fastanúmer:" value={vl.fastanumer || v.fastanumer} />
        <FRow label="Skráningarnúmer:" value={vl.plate_number || vl.fastanumer} />
        <FRow label="Tegund:" value={vl.tegund || v.displayName} />
        <FRow label="Undirtegund:" value={vl.undirtegund || v.subtype} />
        <FRow label="Litur:" value={vl.color || v.color} />
        <FRow label="Fyrsta skráning:" value={fmtDate(vl.fyrsta_skraning)} />
        <FRow label="Verksmiðjunúmer:" value={vl.vin || v.vin} />
        <FRow label="Framleiðandi:" value={vl.manufacturer} />
        <FRow label="Skráningarflokkur:" value={fer.registrationType} />
        <FRow label="Notkunarflokkur:" value={vl.usage_category} />
        <FRow label="Ökutækisflokkur:" value={tech.vehicleCategory} />
        <FRow label="Innflutningsástand:" value={vl.import_condition} />
        <FRow label="Vélargerð:" value={tech.energySource} />
        <FRow label="Slagrými:" value={tech.displacement ? `${tech.displacement} cm³` : ""} />
        <FRow label="Afköst:" value={tech.power ? `${tech.power} kW` : ""} />
        <FRow label="Upprunaland:" value={vl.country_of_manufacture} />
        <FRow label="Áður skráð í:" value={vl.previous_country} />
        <FRow label="Staða:" value={vl.vehicle_status} />
      </div>
      <hr style={HR_LIGHT} />

      {/* 2. Greiðslustaða */}
      <div style={FS_SH}>2. Greiðslustaða</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <FRow label="Tryggingafélag:" value={vl.insurance_company} />
        <FRow label="Tryggður:" value={vl.is_insured ? "Já" : "Nei"} />
        <FRow label="Bifreiðagjald:" value={fees.vehicleTax ? `kr. ${formatIcelandicNumber(fees.vehicleTax)}.-` : ""} />
        <FRow label="Þungaskattur:" value={fees.weightTax ? `kr. ${formatIcelandicNumber(fees.weightTax)}.-` : ""} />
        <FRow label="Veðbönd:" value={vl.has_encumbrances ? "Já" : "Nei"} />
      </div>
      <hr style={HR_LIGHT} />

      {/* 3. Eigandaupplýsingar */}
      <div style={FS_SH}>3. Eigandaupplýsingar</div>
      {owners.length > 0 && (
        <div>
          <FRow label="Aðaleigandi:" value={`${owners[0].name} (kt. ${owners[0].regno})`} />
          <FRow label="Heimilisfang:" value={`${owners[0].address}, ${owners[0].postalCode} ${owners[0].city}`} />
        </div>
      )}
      {operators.length > 0 && (
        <FRow label="Umráðamaður:" value={`${operators[0].name} (kt. ${operators[0].regno})`} />
      )}
      <hr style={HR_LIGHT} />

      {/* 4. Eigendaferill */}
      <div style={FS_SH}>4. Eigendaferill</div>
      {owners.length > 0 ? (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={FS_TH}>Kaupdagur</th>
              <th style={FS_TH}>Skráning</th>
              <th style={FS_TH}>Nafn</th>
              <th style={FS_TH}>Heimili</th>
            </tr>
          </thead>
          <tbody>
            {owners.map((o, i) => (
              <tr key={i}>
                <td style={FS_TD}>{fmtDate(o.purchaseDate)}</td>
                <td style={FS_TD}>{fmtDate(o.registrationDate)}</td>
                <td style={FS_TD}>{o.name}</td>
                <td style={FS_TD}>{o.address}{o.postalCode ? `, ${o.postalCode}` : ""} {o.city}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ color: "#666", fontStyle: "italic" }}>Engar upplýsingar.</div>}
      <hr style={HR_LIGHT} />

      {/* 5. Umráðaferill */}
      <div style={FS_SH}>5. Umráðaferill</div>
      {operators.length > 0 ? (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={FS_TH}>Dags frá</th>
              <th style={FS_TH}>Dags til</th>
              <th style={FS_TH}>Nafn</th>
              <th style={FS_TH}>Heimili</th>
            </tr>
          </thead>
          <tbody>
            {operators.map((op, i) => (
              <tr key={i}>
                <td style={FS_TD}>{fmtDate(op.dateFrom)}</td>
                <td style={FS_TD}>{fmtDate(op.dateTo)}</td>
                <td style={FS_TD}>{op.name}</td>
                <td style={FS_TD}>{op.address}{op.postalCode ? `, ${op.postalCode}` : ""} {op.city}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ color: "#666", fontStyle: "italic" }}>Engir umráðamenn skráðir.</div>}
      <hr style={HR_LIGHT} />

      {/* 6. Skráningarferill */}
      <div style={FS_SH}>6. Skráningarferill</div>
      {registrations.length > 0 ? (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={FS_TH}>Dagsetning</th>
              <th style={FS_TH}>Tegund skráningar</th>
            </tr>
          </thead>
          <tbody>
            {registrations.map((r, i) => (
              <tr key={i}>
                <td style={FS_TD}>{fmtDate(r.date)}</td>
                <td style={FS_TD}>{r.type}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ color: "#666", fontStyle: "italic" }}>Engar skráningar.</div>}
      <hr style={HR_LIGHT} />

      {/* 7. Tjónaferill */}
      <div style={FS_SH}>7. Tjónaferill</div>
      {damages.length > 0 ? (
        <div>{damages.map((d, i) => <div key={i}>{JSON.stringify(d)}</div>)}</div>
      ) : <div style={{ color: "#666", fontStyle: "italic" }}>Engin tjón skráð.</div>}

      {/* 8. Slysaferill */}
      <div style={FS_SH}>8. Slysaferill</div>
      <div style={{ color: "#666", fontStyle: "italic" }}>
        {vl.has_accidents
          ? "Slys skráð á ökutæki."
          : `Engin óhöpp eru skráð á ${vl.fastanumer || v.fastanumer} í slysaskrá.`}
      </div>
      <hr style={HR_LIGHT} />

      {/* 9. Skoðunarferill */}
      <div style={FS_SH}>9. Skoðunarferill</div>
      {inspections.length > 0 ? (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={FS_TH}>Dagsetning</th>
              <th style={FS_TH}>Skoðunarstaður</th>
              <th style={FS_TH}>Tegund</th>
              <th style={FS_TH}>Niðurstaða</th>
              <th style={FS_TH}>Ökumælis</th>
            </tr>
          </thead>
          <tbody>
            {inspections.map((ins, i) => (
              <tr key={i}>
                <td style={FS_TD}>{fmtDate(ins.date)}</td>
                <td style={FS_TD}>{ins.location}</td>
                <td style={FS_TD}>{ins.type}</td>
                <td style={FS_TD}>{ins.result}</td>
                <td style={FS_TD}>{ins.mileage != null ? formatIcelandicNumber(ins.mileage) : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ color: "#666", fontStyle: "italic" }}>Engar skoðanir skráðar.</div>}
      <hr style={HR_LIGHT} />

      {/* 10. Breytingaferill */}
      <div style={FS_SH}>10. Breytingaferill</div>
      {changes.length > 0 ? (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={FS_TH}>Dagsetning</th>
              <th style={FS_TH}>Breytt atriði</th>
              <th style={FS_TH}>Fyrir breytingu</th>
              <th style={FS_TH}>Eftir breytingu</th>
            </tr>
          </thead>
          <tbody>
            {changes.map((c, i) => (
              <tr key={i}>
                <td style={FS_TD}>{fmtDate(c.date || c.changeDate)}</td>
                <td style={FS_TD}>{c.description || c.changeDescription || ""}</td>
                <td style={FS_TD}>{c.oldValue || c.previousValue || ""}</td>
                <td style={FS_TD}>{c.newValue || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ color: "#666", fontStyle: "italic" }}>Engar breytingar skráðar.</div>}

      <Footer createdBy={form?.general?.createdBy} refNum={form?.general?.referenceNumber} />
    </div>
  );
};

export default PageThree;
